/**
 * Cost of Delay tool runner.
 * Calculates Cost of Delay = Value Lost + Cost to Restart.
 */

import type {
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
} from "../harmonizer/types";
import type { NativeScoreSet } from "../harmonizer/testHarness";
import type { RawToolSpec } from "../harmonizer/specLoader";
import { loadToolSpecFile, getToolSpecPath } from "./specLoader";

let cachedSpec: RawToolSpec | null = null;

/**
 * Gets the raw tool spec for Cost of Delay.
 */
export function getCostOfDelaySpec(): RawToolSpec {
  if (cachedSpec) {
    return cachedSpec;
  }

  const specPath = getToolSpecPath("cost-of-delay");
  cachedSpec = loadToolSpecFile(specPath);
  return cachedSpec;
}

/**
 * Runs the Cost of Delay tool.
 * 
 * @param items - Items to score
 * @param _criteriaSet - CriteriaSet (not used, but can contain value_lost/cost_to_restart criteria)
 * @param toolConfig - Tool configuration (value_scale, allow_zero_values)
 * @param lens - Optional lens for filtering
 * @returns Native score set
 */
export async function runCostOfDelayTool(
  items: Item[],
  _criteriaSet: CriteriaSet | null,
  toolConfig: ToolConfig,
  lens?: Lens | null
): Promise<NativeScoreSet> {
  const toolRunId = `cost-of-delay-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Get config values
  const allowZeroValues = (toolConfig.allow_zero_values as boolean) ?? true;

  // Filter items if lens is provided
  let filteredItems = items;
  if (lens) {
    const { applyLens } = await import("../arrange/validation");
    filteredItems = applyLens(items, lens);
  }

  const scoredItems: Array<{
    item_id: string;
    score: number;
    rank: number;
    value_lost: number;
    cost_to_restart: number;
  }> = [];
  const itemsWithScores: Array<{
    item: Item;
    score: number;
    valueLost: number;
    costToRestart: number;
  }> = [];
  const excludedItems: string[] = [];

  for (const item of filteredItems) {
    const valueLost = item.attributes.value_lost;
    const costToRestart = item.attributes.cost_to_restart;

    // Validate required attributes
    if (valueLost === null || valueLost === undefined) {
      excludedItems.push(`${item.id}: missing value_lost`);
      continue;
    }
    if (costToRestart === null || costToRestart === undefined) {
      excludedItems.push(`${item.id}: missing cost_to_restart`);
      continue;
    }

    const valueLostNum = typeof valueLost === "number" ? valueLost : Number(valueLost);
    const costToRestartNum = typeof costToRestart === "number" ? costToRestart : Number(costToRestart);

    if (isNaN(valueLostNum) || (!allowZeroValues && valueLostNum <= 0)) {
      excludedItems.push(`${item.id}: invalid value_lost`);
      continue;
    }
    if (isNaN(costToRestartNum) || (!allowZeroValues && costToRestartNum <= 0)) {
      excludedItems.push(`${item.id}: invalid cost_to_restart`);
      continue;
    }

    if (valueLostNum < 0 || costToRestartNum < 0) {
      excludedItems.push(`${item.id}: negative values not allowed`);
      continue;
    }

    // Calculate Cost of Delay
    const score = valueLostNum + costToRestartNum;

    itemsWithScores.push({
      item,
      score,
      valueLost: valueLostNum,
      costToRestart: costToRestartNum,
    });
  }

  // Sort by score (descending), then by label (ascending) for tie-breaking
  itemsWithScores.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    return a.item.label.localeCompare(b.item.label);
  });

  // Assign ranks and build output
  let currentRank = 1;
  for (let i = 0; i < itemsWithScores.length; i++) {
    const { item, score, valueLost, costToRestart } = itemsWithScores[i];

    // If score differs from previous, update rank
    if (i > 0 && itemsWithScores[i - 1].score !== score) {
      currentRank = i + 1;
    }

    scoredItems.push({
      item_id: item.id,
      score: score,
      rank: currentRank,
      value_lost: valueLost,
      cost_to_restart: costToRestart,
    });
  }

  return {
    items: scoredItems,
    toolRunId,
    metadata: {
      total_items: filteredItems.length,
      valid_items: scoredItems.length,
      items_excluded: excludedItems.length,
      exclusion_reasons: excludedItems,
    },
  };
}


